import type { ReactNode } from "react";
import { ROUTE_CODE, ROUTE_TAKES } from "../_content/pages";
import type { ApiRoute } from "../_lib/reference/api";
import { DOOR_LANG } from "../_lib/highlight";
import { httpCall } from "../_lib/reference/curl";
import type { RouteCode } from "../_lib/reference/route-samples";
import { Bar } from "./Bar";
import { Chev } from "./Chev";
import { CodeLines } from "./CodeLines";
import { Copy } from "./Copy";
import { Docked } from "./Docked";
import { FieldList, ShapeList } from "./Fields";
import { LANGS } from "./lang";

/*
 * A route's fields and code, as its group's page renders them for the route that comes open, and the page
 * renders them in the browser for every other route when it opens (LazyRouteBody), from the group's static
 * JSON: so nothing here reads the disk.
 */

const asParam = (p: ApiRoute["path_params"][number]) => ({
  path: p.name,
  name: p.name,
  depth: 0,
  type: p.type,
  required: p.required,
  description: p.description,
  default: null,
  limits: null,
  shapes: null,
  examples: null,
});

/** A group of fields behind one line: "Request, 9 fields". */
function Group({
  title,
  n,
  children,
}: {
  title: string;
  n: number;
  children: ReactNode;
}) {
  return (
    <details className="grp">
      <summary>
        {title}
        {n > 0 ? (
          <small>
            {n} {n === 1 ? "field" : "fields"}
          </small>
        ) : null}
        <Chev />
      </summary>
      <div className="grp-in">{children}</div>
    </details>
  );
}

/** The top-level fields of a shape, every shape's together, or 1 for a single value: what a group's line counts. */
const countOf = (shape: ApiRoute["answer"]["shape"]): number =>
  !shape
    ? 0
    : shape.kind === "fields"
      ? shape.fields.filter((f) => f.depth === 0).length
      : shape.kind === "oneOf"
        ? shape.shapes.reduce(
            (n, s) => n + s.fields.filter((f) => f.depth === 0).length,
            0
          )
        : 1;

/**
 * A route's code in every door, each window once: the route's own call in each door that has one (the SDK's
 * method, or the CLI's command, T13e-2d), the checked sample's HTTP call, and MCP's where the sample is the
 * route's own line's (codeFor keeps only those); and one HTTP window (the sample's call, or one made from the
 * contract) that every door with none shows, under one quiet line.
 */
export function RouteVariants({
  route,
  code,
}: {
  route: ApiRoute;
  code: RouteCode;
}) {
  const own = LANGS.flatMap(({ door }) => {
    const text =
      code.calls[door] ?? code.sample?.doors.find((d) => d.door === door)?.code;
    return text ? [{ door, text }] : [];
  });
  const rest = LANGS.map((l) => l.door).filter(
    (door) => !own.some((o) => o.door === door)
  );
  const http = httpCall(route, code);
  return (
    <>
      {own.map(({ door, text }) => (
        <div key={door} className="lc" data-l={door}>
          <Bar door={door} />
          <Copy text={text} />
          <CodeLines code={text} lang={DOOR_LANG[door]} />
        </div>
      ))}
      {rest.length > 0 ? (
        <div className="lc-rest" data-rest={rest.join(" ")}>
          <Bar door="http" />
          <Copy text={http} />
          <p className="fallback">{ROUTE_CODE.overHttp}</p>
          <CodeLines code={http} lang="bash" />
        </div>
      ) : null}
    </>
  );
}

/** What a route takes and what it answers, each a group of one-line fields, then its code. */
export function RouteBody({
  route,
  code,
}: {
  route: ApiRoute;
  code: RouteCode;
}) {
  const params = [
    { title: "Path", list: route.path_params },
    { title: "Query", list: route.query },
    { title: "Headers", list: route.headers },
  ].filter((p) => p.list.length > 0);
  const requestCount =
    params.reduce((n, p) => n + p.list.length, 0) +
    (route.body ? countOf(route.body.shape) : 0);
  const stream = ROUTE_TAKES.answer(route.method, route.answer.media[0]);
  return (
    <>
      {requestCount > 0 ? (
        <Group title="Request" n={requestCount}>
          {params.map((p) => (
            <div key={p.title}>
              <p className="sub">{p.title}</p>
              <FieldList fields={p.list.map(asParam)} />
            </div>
          ))}
          {route.body ? (
            <div>
              <p className="sub">
                {ROUTE_TAKES.body(route.body.media, route.body.required)}
              </p>
              <ShapeList shape={route.body.shape} />
            </div>
          ) : null}
        </Group>
      ) : (
        <p className="r-meta">{ROUTE_TAKES.nothing}</p>
      )}
      <Group
        title={`Answers ${route.answer.statuses.join(" or ")}`}
        n={countOf(route.answer.shape)}
      >
        {stream ? <p className="r-meta">{stream}</p> : null}
        {route.answer.shape ? (
          <ShapeList shape={route.answer.shape} sent />
        ) : stream ? null : (
          <p className="r-meta">{ROUTE_TAKES.protocol}</p>
        )}
      </Group>
      <Docked frame={route.anchor}>
        <RouteVariants route={route} code={code} />
      </Docked>
    </>
  );
}
